import * as React from "react";
import {
  Alert,
  Box,
  Button,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  FormControl,
  IconButton,
  InputAdornment,
  InputLabel,
  MenuItem,
  Select,
  TextField,
  Tooltip,
  Typography,
} from "@mui/material";
import {
  DataGrid,
  GridActionsCellItem,
  GridColDef,
  GridPaginationModel,
} from "@mui/x-data-grid";
import { itIT } from "@mui/x-data-grid/locales";
import SearchIcon from "@mui/icons-material/Search";
import EditIcon from "@mui/icons-material/Edit";
import DeleteIcon from "@mui/icons-material/DeleteOutlined";
import CloseIcon from "@mui/icons-material/Close";
import toast from "react-hot-toast";
import { useMutation, useQuery, useQueryClient } from "react-query";
import { useAuth } from "../contexts/AuthContext";
import { ConfirmDialog } from "../components/common";
import {
  DistanceEntry,
  deleteDistanceEntry,
  listDistanceEntries,
  updateDistanceEntry,
} from "../services/api";

const QUERY_KEY = "distance-entries";

const VETTORI = ["GOMMA", "TRENO", "NAVE", "AEREO"];

const fmtKm = (v: number | null | undefined) =>
  v === null || v === undefined ? "" : Math.round(v).toLocaleString("it-IT");

export const DistancesDatabasePage: React.FC = () => {
  const { user } = useAuth();
  const queryClient = useQueryClient();

  const hasPermission = user?.role === "admin" || user?.role === "mantainer";

  const [search, setSearch] = React.useState("");
  const [searchInput, setSearchInput] = React.useState("");
  const [vettoreFilter, setVettoreFilter] = React.useState<string>("");
  const [alertFilter, setAlertFilter] = React.useState<string>(""); // "", "true", "false"
  const [paginationModel, setPaginationModel] = React.useState<GridPaginationModel>({
    page: 0,
    pageSize: 25,
  });

  // Debounce della ricerca testuale
  React.useEffect(() => {
    const t = setTimeout(() => setSearch(searchInput), 350);
    return () => clearTimeout(t);
  }, [searchInput]);

  React.useEffect(() => {
    setPaginationModel((p) => ({ ...p, page: 0 }));
  }, [search, vettoreFilter, alertFilter]);

  const queryParams = {
    q: search || undefined,
    vettore: vettoreFilter || undefined,
    has_alert: alertFilter === "" ? undefined : alertFilter === "true",
    page: paginationModel.page,
    page_size: paginationModel.pageSize,
  };

  const { data, isLoading, isFetching, error } = useQuery(
    [QUERY_KEY, queryParams],
    () => listDistanceEntries(queryParams),
    { keepPreviousData: true, enabled: hasPermission }
  );

  const [editingRow, setEditingRow] = React.useState<DistanceEntry | null>(null);
  const [editValues, setEditValues] = React.useState<Record<string, any>>({});
  const [deleteTarget, setDeleteTarget] = React.useState<DistanceEntry | null>(null);

  const updateMutation = useMutation(
    (payload: { key: string; data: Record<string, unknown> }) =>
      updateDistanceEntry(payload.key, payload.data),
    {
      onSuccess: () => {
        queryClient.invalidateQueries(QUERY_KEY);
        toast.success("Voce aggiornata con successo");
        setEditingRow(null);
      },
      onError: (err: any) => {
        toast.error(err?.response?.data?.detail || "Aggiornamento non riuscito");
      },
    }
  );

  const deleteMutation = useMutation(deleteDistanceEntry, {
    onSuccess: () => {
      queryClient.invalidateQueries(QUERY_KEY);
      toast.success("Voce eliminata con successo");
      setDeleteTarget(null);
    },
    onError: (err: any) => {
      toast.error(err?.response?.data?.detail || "Eliminazione non riuscita");
      setDeleteTarget(null);
    },
  });

  if (!hasPermission) {
    return (
      <Box sx={{ p: 2 }}>
        <Alert severity="error">
          Non hai i permessi per accedere a questa sezione. Solo gli
          amministratori e i mantainer possono consultare il database tratte.
        </Alert>
      </Box>
    );
  }

  const handleOpenEdit = (row: DistanceEntry) => {
    setEditingRow(row);
    setEditValues({ ...row });
  };

  const handleCloseEdit = () => {
    setEditingRow(null);
    setEditValues({});
  };

  const handleSaveEdit = () => {
    if (!editingRow) return;
    const payload: Record<string, unknown> = {};
    const fieldMap: Record<string, string> = {
      a_azienda: "A_azienda", a_location: "A_location", a_porto: "A_porto",
      a_location_lat: "A_location_lat", a_location_lon: "A_location_lon",
      a_porto_lat: "A_porto_lat", a_porto_lon: "A_porto_lon",
      b_azienda: "B_azienda", b_location: "B_location", b_porto: "B_porto",
      b_location_lat: "B_location_lat", b_location_lon: "B_location_lon",
      b_porto_lat: "B_porto_lat", b_porto_lon: "B_porto_lon",
      vettore: "vettore", alert: "alert", assunzioni: "assunzioni",
      km_gomma: "km_gomma", km_treno: "km_treno", km_nave: "km_nave", km_aereo: "km_aereo",
      first_mile: "first_mile", last_mile: "last_mile",
    };
    Object.entries(fieldMap).forEach(([lowerKey, apiKey]) => {
      const original = (editingRow as any)[lowerKey];
      const current = editValues[lowerKey];
      const normalizedCurrent = current === "" ? null : current;
      if (normalizedCurrent !== (original ?? null)) {
        payload[apiKey] = normalizedCurrent;
      }
    });
    if (Object.keys(payload).length === 0) {
      handleCloseEdit();
      return;
    }
    updateMutation.mutate({ key: editingRow.cache_key_l1, data: payload });
  };

  const handleFieldChange = (field: string, value: any) => {
    setEditValues((prev) => ({ ...prev, [field]: value }));
  };

  const columns: GridColDef[] = [
    { field: "a_azienda", headerName: "Azienda A", width: 140 },
    { field: "a_location", headerName: "Sede A", width: 170 },
    { field: "a_porto", headerName: "Hub A", width: 90 },
    { field: "b_azienda", headerName: "Azienda B", width: 140 },
    { field: "b_location", headerName: "Sede B", width: 170 },
    { field: "b_porto", headerName: "Hub B", width: 90 },
    {
      field: "vettore", headerName: "Vettore", width: 100,
      renderCell: (params) =>
        params.value ? <Chip label={params.value} size="small" /> : null,
    },
    { field: "km_gomma", headerName: "Km Gomma", width: 100, valueFormatter: (v: any) => fmtKm(v) },
    { field: "km_treno", headerName: "Km Treno", width: 100, valueFormatter: (v: any) => fmtKm(v) },
    { field: "km_nave", headerName: "Km Nave", width: 100, valueFormatter: (v: any) => fmtKm(v) },
    { field: "km_aereo", headerName: "Km Aereo", width: 100, valueFormatter: (v: any) => fmtKm(v) },
    {
      field: "alert", headerName: "Alert", width: 90,
      renderCell: (params) =>
        params.value ? (
          <Tooltip title={params.value}>
            <Chip label="⚠" size="small" color="warning" />
          </Tooltip>
        ) : null,
    },
    {
      field: "actions", type: "actions", headerName: "Azioni", width: 100,
      getActions: (params) => [
        <GridActionsCellItem
          key="edit"
          icon={<EditIcon />}
          label="Modifica"
          onClick={() => handleOpenEdit(params.row as DistanceEntry)}
        />,
        <GridActionsCellItem
          key="delete"
          icon={<DeleteIcon />}
          label="Elimina"
          onClick={() => setDeleteTarget(params.row as DistanceEntry)}
        />,
      ],
    },
  ];

  const rows = (data?.items || []).map((r) => ({ ...r, id: r.cache_key_l1 }));

  return (
    <Box sx={{ p: 2, height: "100%", display: "flex", flexDirection: "column" }}>
      {/* Ricerca + filtri */}
      <Box sx={{ display: "flex", gap: 2, mb: 2, flexWrap: "wrap", flexShrink: 0 }}>
        <TextField
          size="small"
          placeholder="Cerca per azienda, sede, porto…"
          value={searchInput}
          onChange={(e) => setSearchInput(e.target.value)}
          sx={{ minWidth: 280 }}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <SearchIcon fontSize="small" />
              </InputAdornment>
            ),
          }}
        />
        <FormControl size="small" sx={{ minWidth: 150 }}>
          <InputLabel>Vettore</InputLabel>
          <Select
            label="Vettore"
            value={vettoreFilter}
            onChange={(e) => setVettoreFilter(e.target.value)}
          >
            <MenuItem value="">Tutti</MenuItem>
            {VETTORI.map((v) => (
              <MenuItem key={v} value={v}>{v}</MenuItem>
            ))}
          </Select>
        </FormControl>
        <FormControl size="small" sx={{ minWidth: 170 }}>
          <InputLabel>Alert</InputLabel>
          <Select
            label="Alert"
            value={alertFilter}
            onChange={(e) => setAlertFilter(e.target.value)}
          >
            <MenuItem value="">Tutte le voci</MenuItem>
            <MenuItem value="true">Solo con alert</MenuItem>
            <MenuItem value="false">Solo senza alert</MenuItem>
          </Select>
        </FormControl>
      </Box>

      {Boolean(error) && (
        <Alert severity="error" sx={{ mb: 2, flexShrink: 0 }}>
          Errore nel caricamento dei dati.
        </Alert>
      )}

      <Box sx={{ flexGrow: 1, minHeight: 0 }}>
        <DataGrid
          rows={rows}
          columns={columns}
          loading={isLoading || isFetching}
          paginationMode="server"
          rowCount={data?.total || 0}
          paginationModel={paginationModel}
          onPaginationModelChange={setPaginationModel}
          pageSizeOptions={[25, 50, 100]}
          disableRowSelectionOnClick
          sx={{ borderRadius: 2 }}
          localeText={itIT.components.MuiDataGrid.defaultProps.localeText}
        />
      </Box>

      {/* Dialog di modifica */}
      <Dialog open={!!editingRow} onClose={handleCloseEdit} maxWidth="md" fullWidth>
        <DialogTitle sx={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
          Modifica voce
          <IconButton onClick={handleCloseEdit} size="small"><CloseIcon /></IconButton>
        </DialogTitle>
        <DialogContent dividers>
          {editingRow && (
            <Box sx={{ display: "flex", flexDirection: "column", gap: 3, pt: 1 }}>
              <Alert severity="info" sx={{ borderRadius: "10px" }}>
                Se modifichi azienda, sede, porto o vettore, la voce viene identificata
                con una nuova chiave e le indicizzazioni interne (cache) vengono
                ricalcolate automaticamente al salvataggio.
              </Alert>

              <Box>
                <Typography variant="subtitle2" sx={{ mb: 1, fontWeight: 600 }}>Origine (A)</Typography>
                <Box sx={{ display: "grid", gridTemplateColumns: "repeat(3, 1fr)", gap: 2 }}>
                  <TextField label="Azienda A" size="small" value={editValues.a_azienda || ""} onChange={(e) => handleFieldChange("a_azienda", e.target.value)} />
                  <TextField label="Sede A" size="small" value={editValues.a_location || ""} onChange={(e) => handleFieldChange("a_location", e.target.value)} />
                  <TextField label="Porto/Aeroporto A" size="small" value={editValues.a_porto || ""} onChange={(e) => handleFieldChange("a_porto", e.target.value)} />
                  <TextField label="Lat sede A" size="small" type="number" value={editValues.a_location_lat ?? ""} onChange={(e) => handleFieldChange("a_location_lat", e.target.value === "" ? "" : parseFloat(e.target.value))} />
                  <TextField label="Lon sede A" size="small" type="number" value={editValues.a_location_lon ?? ""} onChange={(e) => handleFieldChange("a_location_lon", e.target.value === "" ? "" : parseFloat(e.target.value))} />
                  <span />
                  <TextField label="Lat hub A" size="small" type="number" value={editValues.a_porto_lat ?? ""} onChange={(e) => handleFieldChange("a_porto_lat", e.target.value === "" ? "" : parseFloat(e.target.value))} />
                  <TextField label="Lon hub A" size="small" type="number" value={editValues.a_porto_lon ?? ""} onChange={(e) => handleFieldChange("a_porto_lon", e.target.value === "" ? "" : parseFloat(e.target.value))} />
                </Box>
              </Box>

              <Divider />

              <Box>
                <Typography variant="subtitle2" sx={{ mb: 1, fontWeight: 600 }}>Destinazione (B)</Typography>
                <Box sx={{ display: "grid", gridTemplateColumns: "repeat(3, 1fr)", gap: 2 }}>
                  <TextField label="Azienda B" size="small" value={editValues.b_azienda || ""} onChange={(e) => handleFieldChange("b_azienda", e.target.value)} />
                  <TextField label="Sede B" size="small" value={editValues.b_location || ""} onChange={(e) => handleFieldChange("b_location", e.target.value)} />
                  <TextField label="Porto/Aeroporto B" size="small" value={editValues.b_porto || ""} onChange={(e) => handleFieldChange("b_porto", e.target.value)} />
                  <TextField label="Lat sede B" size="small" type="number" value={editValues.b_location_lat ?? ""} onChange={(e) => handleFieldChange("b_location_lat", e.target.value === "" ? "" : parseFloat(e.target.value))} />
                  <TextField label="Lon sede B" size="small" type="number" value={editValues.b_location_lon ?? ""} onChange={(e) => handleFieldChange("b_location_lon", e.target.value === "" ? "" : parseFloat(e.target.value))} />
                  <span />
                  <TextField label="Lat hub B" size="small" type="number" value={editValues.b_porto_lat ?? ""} onChange={(e) => handleFieldChange("b_porto_lat", e.target.value === "" ? "" : parseFloat(e.target.value))} />
                  <TextField label="Lon hub B" size="small" type="number" value={editValues.b_porto_lon ?? ""} onChange={(e) => handleFieldChange("b_porto_lon", e.target.value === "" ? "" : parseFloat(e.target.value))} />
                </Box>
              </Box>

              <Divider />

              <Box>
                <Typography variant="subtitle2" sx={{ mb: 1, fontWeight: 600 }}>Vettore e distanze (km)</Typography>
                <Box sx={{ display: "grid", gridTemplateColumns: "repeat(3, 1fr)", gap: 2 }}>
                  <FormControl size="small">
                    <InputLabel>Vettore</InputLabel>
                    <Select
                      label="Vettore"
                      value={editValues.vettore || ""}
                      onChange={(e) => handleFieldChange("vettore", e.target.value)}
                    >
                      <MenuItem value="">—</MenuItem>
                      {VETTORI.map((v) => <MenuItem key={v} value={v}>{v}</MenuItem>)}
                    </Select>
                  </FormControl>
                  <TextField label="Km Gomma" size="small" type="number" value={editValues.km_gomma ?? ""} onChange={(e) => handleFieldChange("km_gomma", e.target.value === "" ? "" : parseFloat(e.target.value))} />
                  <TextField label="Km Treno" size="small" type="number" value={editValues.km_treno ?? ""} onChange={(e) => handleFieldChange("km_treno", e.target.value === "" ? "" : parseFloat(e.target.value))} />
                  <TextField label="Km Nave" size="small" type="number" value={editValues.km_nave ?? ""} onChange={(e) => handleFieldChange("km_nave", e.target.value === "" ? "" : parseFloat(e.target.value))} />
                  <TextField label="Km Aereo" size="small" type="number" value={editValues.km_aereo ?? ""} onChange={(e) => handleFieldChange("km_aereo", e.target.value === "" ? "" : parseFloat(e.target.value))} />
                  <span />
                  <TextField label="First mile" size="small" type="number" value={editValues.first_mile ?? ""} onChange={(e) => handleFieldChange("first_mile", e.target.value === "" ? "" : parseFloat(e.target.value))} />
                  <TextField label="Last mile" size="small" type="number" value={editValues.last_mile ?? ""} onChange={(e) => handleFieldChange("last_mile", e.target.value === "" ? "" : parseFloat(e.target.value))} />
                </Box>
              </Box>

              <Divider />

              <Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
                <TextField label="Alert" size="small" multiline minRows={2} value={editValues.alert || ""} onChange={(e) => handleFieldChange("alert", e.target.value)} />
                <TextField label="Assunzioni" size="small" multiline minRows={2} value={editValues.assunzioni || ""} onChange={(e) => handleFieldChange("assunzioni", e.target.value)} />
              </Box>
            </Box>
          )}
        </DialogContent>
        <DialogActions sx={{ p: 2 }}>
          <Button onClick={handleCloseEdit} disabled={updateMutation.isLoading}>Annulla</Button>
          <Button variant="contained" onClick={handleSaveEdit} disabled={updateMutation.isLoading}>
            Salva
          </Button>
        </DialogActions>
      </Dialog>

      {/* Conferma eliminazione */}
      <ConfirmDialog
        open={!!deleteTarget}
        loading={deleteMutation.isLoading}
        onCancel={() => setDeleteTarget(null)}
        onConfirm={() => deleteTarget && deleteMutation.mutate(deleteTarget.cache_key_l1)}
        title="Conferma eliminazione"
        content={`Eliminare definitivamente la tratta ${deleteTarget?.a_location || ""} → ${deleteTarget?.b_location || ""}?`}
        confirmText="Elimina"
        cancelText="Annulla"
      />
    </Box>
  );
};
